import React, { useContext, useState } from "react"
import { motion } from "framer-motion"
import cc from "classcat"
import { LocaleContext } from "contexts"
import useTranslations from "hooks/useTranslations"
import SectionContainer from "components/molecules/common/SectionContainer"
import VideoWithTitle from "components/molecules/VideoWithTitle"
import ScrollAnimate from "components/atoms/common/ScrollAnimate"
import H3 from "components/atoms/H3"

import countries from "../../../config/i18n"
import { siteCountry } from "../../utils/env"
import Button from "components/buttons/Button"
import Modal from "components/molecules/Modal"

const HeroButton = ({ theme = "green", onClick, buttonAction }) => {
  const { locale } = useContext(LocaleContext)
  const { buttonLoginHelp, buttonOpenVideo } = useTranslations()
  return (
    <Button
      bgStyle={theme}
      label={buttonAction === "openVideo" ? buttonOpenVideo : buttonLoginHelp}
      link={
        buttonAction === "openVideo"
          ? ""
          : countries[siteCountry][locale].appLink
      }
      outbound={buttonAction === "openVideo" ? false : true}
      className="animate-scale"
      onClick={onClick}
    />
  )
}

interface IntroProps {
  title: string
  videoLink: string
  descriptions: string[]
  theme: string
  size?: "large" | "small"
  buttonAction?: "openVideo" | "appLink"
}

export const Intro: React.FC<IntroProps> = ({
  title,
  videoLink,
  descriptions,
  theme,
  size = "large",
  buttonAction = "appLink",
}) => {
  const styles = {
    dark: {
      bg: "bg-zinc-800",
      text: "light",
      description: "text-zinc-100",
      heroButton: "green",
    },
    light: {
      bg: "bg-kkm-green/90",
      text: "dark",
      description: "text-zinc-800",
      heroButton: "black",
    },
    blue: {
      bg: "bg-kkm-blue hover:bg-kkm-blue/100",
      text: "light",
      description: "text-zinc-100",
      heroButton: "yellow",
    },
    red: {
      bg: "bg-kkm-red/80",
      text: "dark",
      description: "text-zinc-800",
      heroButton: "black",
    },
  }
  const [show, setShow] = useState(false)

  return (
    <SectionContainer className={cc([styles[theme].bg])} paddingSize={size}>
      <div className={cc(["space-y-8", size === "small" && "max-w-4xl"])}>
        <ScrollAnimate>
          <H3
            size={size === "small" ? "medium" : "large"}
            textAlign="left"
            textStyle={styles[theme].text}
          >
            {title}
          </H3>
        </ScrollAnimate>
        <div className="flex flex-col gap-10 lg:grid lg:grid-cols-2">
          <ScrollAnimate delay={0.6}>
            <div className="flex flex-col">
              <VideoWithTitle verticalMobile link={videoLink} />
            </div>
          </ScrollAnimate>
          <div className="col-span-8 mx-auto flex w-full flex-col justify-between lg:col-span-1">
            <div className={styles[theme].description}>
              {descriptions.map((description, index) => (
                <ScrollAnimate key={index} delay={0.6 + index * 0.4}>
                  <p
                    className={cc([
                      "mb-4",
                      descriptions.length >= 3 && "text-base",
                      descriptions.length < 3 && "text-xl",
                    ])}
                  >
                    {description}
                  </p>
                </ScrollAnimate>
              ))}
            </div>
            <ScrollAnimate delay={1.4}>
              <div className="justify-right mt-8 flex w-full">
                <HeroButton
                  theme={styles[theme].heroButton}
                  onClick={
                    buttonAction === "openVideo" ? () => setShow(true) : false
                  }
                  buttonAction={buttonAction}
                />
              </div>
            </ScrollAnimate>
          </div>
        </div>
      </div>
      <Modal
        isOpen={show}
        onRequestClose={() => setShow(false)}
        showCloseButton={false}
        closeOnClickAway={true}
      >
        <iframe
          className="mb-0 aspect-video rounded-lg sm:w-[600px]  md:w-[700px]"
          src={`${videoLink}?autoplay=1&rel=0&cc_load_policy=1`}
          allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        ></iframe>
      </Modal>
    </SectionContainer>
  )
}

interface OnboardingSectionProps {
  id: string
  title: string
  descriptions: string[]
  videoLink: string
  theme: "dark" | "light" | "blue" | "red"
  size?: "large" | "small"
  buttonAction?: "openVideo" | "appLink"
}

const OnboardingSection = ({
  id = "recording",
  title,
  descriptions,
  videoLink,
  theme = "dark",
  size = "large",
  buttonAction,
}: OnboardingSectionProps) => {
  return (
    <motion.section
      layout
      aria-labelledby={title}
      className="scroll-mt-14 lg:scroll-mt-24"
      id={id}
    >
      <Intro
        title={title}
        descriptions={descriptions}
        videoLink={videoLink}
        theme={theme}
        size={size}
        buttonAction={buttonAction}
      />
    </motion.section>
  )
}

export default OnboardingSection
